import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent, Button, Grid } from '../../design-system'
import { X, Share2, Copy, Download, Facebook, Twitter, Linkedin, Mail, Link, Image, Settings, Eye, EyeOff, Lock, MessageCircle, Users } from 'lucide-react'
import { useApi } from '../../hooks/useApi'
import { useResponsive } from '../../hooks/useResponsive'
import DiscordIcon from '../icons/DiscordIcon'
import CopyMessageModal from './CopyMessageModal'

/**
 * Adaptive Share Modal Component
 * Mobile: Full-screen modal with essential sharing options
 * Desktop: Expanded modal with preview and editing options
 * Supports Discord, native mobile sharing (SMS, WhatsApp, etc), and copy fallback
 */

const ShareModal = ({ 
  isOpen, 
  onClose, 
  result = {}, 
  onShare,
  userProfile = null,
  className 
}) => {
  const [shareSettings, setShareSettings] = useState({
    includeScore: true,
    includePercentile: true,
    includeComparison: false,
    includeClassification: true,
    customMessage: '',
    privacy: 'public' // 'public', 'friends', 'private'
  })
  const [copied, setCopied] = useState(false)
  const [isSharing, setIsSharing] = useState(false)
  const [shareUrl, setShareUrl] = useState('')
  const [shareStats, setShareStats] = useState({ views: 0, shares: 0 })
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [copyModalAction, setCopyModalAction] = useState('copyLink') // 'copyLink' or 'discord'

  const { shareResults, apiCall } = useApi()
  const { isMobile } = useResponsive()

  const { iqScore = 120, percentile = 75, classification = 'Above Average', testType = 'free', leaderboardRank } = result || {}

  // Generate share text based on settings
  const generateShareText = (includeRank = false) => {
    // For native sharing with rank-focused message
    if (includeRank && leaderboardRank) {
      return `Hey! I just scored ${iqScore} on this IQ test and I'm #${leaderboardRank} on the leaderboard! 🏆\n\nCan you beat me? Take the test and check out the leaderboard!`
    }

    let text = "I just took an IQ test! 🧠"

    if (shareSettings.includeScore) {
      text = `I just scored ${iqScore} on this IQ test! 🧠`
    }

    if (leaderboardRank) {
      text += ` I'm #${leaderboardRank} on the leaderboard! 🏆`
    }

    if (shareSettings.includePercentile && percentile) {
      text += ` That puts me in the ${percentile}th percentile.`
    }

    if (shareSettings.includeClassification && classification) {
      text += ` My cognitive level is ${classification}.`
    }

    if (shareSettings.customMessage) {
      text += ` ${shareSettings.customMessage}`
    }

    text += " Can you beat my score? Take the test yourself!"
    return text
  }

  const shareText = generateShareText()

  // Load or generate share URL - Always use leaderboard URL
  useEffect(() => {
    if (isOpen) {
      generateShareUrl()
    }
  }, [isOpen, result?.id, shareSettings])

  const generateShareUrl = async () => {
    try {
      // Always use leaderboard URL for sharing
      const leaderboardUrl = `${window.location.origin}/leaderboard`
      setShareUrl(leaderboardUrl)

      // Still track the share for analytics if we have a result
      if (result?.id) {
        const response = await shareResults({
          resultId: result.id,
          settings: shareSettings,
          testType: result.testType || 'free'
        })
        if (response.stats) {
          setShareStats(response.stats)
        }
      }
    } catch (error) {
      // Fallback to leaderboard URL
      setShareUrl(`${window.location.origin}/leaderboard`)
    }
  }

  const handleCopyLink = async () => {
    // Always show the copy modal so users can see the content before copying
    setCopyModalAction('copyLink')
    setShowCopyModal(true)
  }

  // Handle action after copy is successful (e.g., opening Discord)
  const handleCopySuccess = async () => {
    if (copyModalAction === 'discord') {
      // Open Discord after copying the message
      // Use setTimeout to ensure the copy completes before opening Discord
      setTimeout(() => {
        if (isMobile) {
          // On mobile, use window.open with discord:// scheme
          // This is more reliable than location.href on mobile browsers
          const discordOpened = window.open('discord://', '_self')
          // If Discord app doesn't open, fallback to web version
          if (!discordOpened) {
            window.open('https://discord.com/app', '_blank')
          }
        } else {
          // On desktop, open Discord web app in new tab
          window.open('https://discord.com/app', '_blank')
        }
      }, 100)
    }
    // For 'copyLink' action, no additional action needed after copy
  }

  const handleSocialShare = async (platform) => {
    setIsSharing(true)

    try {
      const leaderboardUrl = `${window.location.origin}/leaderboard`

      // Handle native sharing (SMS/WhatsApp)
      if (platform === 'native') {
        const rankMessage = generateShareText(true)
        if (navigator.share) {
          try {
            await navigator.share({
              title: 'IQ Test Challenge - Can You Beat Me?',
              text: rankMessage,
              url: leaderboardUrl
            })
            await trackShareAction('native')
            onShare?.('native', { text: rankMessage, url: leaderboardUrl })
          } catch (error) {
            if (error.name !== 'AbortError') {
              // Fallback to SMS protocol on mobile, otherwise show copy modal
              if (isMobile) {
                const smsBody = encodeURIComponent(`${rankMessage}\n\n${leaderboardUrl}`)
                window.location.href = `sms:?body=${smsBody}`
                await trackShareAction('sms')
              } else {
                setShowCopyModal(true)
              }
            }
          }
        } else {
          // Fallback to SMS protocol on mobile, otherwise show copy modal
          if (isMobile) {
            const smsBody = encodeURIComponent(`${rankMessage}\n\n${leaderboardUrl}`)
            window.location.href = `sms:?body=${smsBody}`
            await trackShareAction('sms')
          } else {
            setShowCopyModal(true)
          }
        }
        setIsSharing(false)
        return
      }

      // Note: Discord will auto-copy and open the app/web, no modal needed

      const encodedText = encodeURIComponent(shareText)
      const encodedUrl = encodeURIComponent(leaderboardUrl)
      const emailBody = encodeURIComponent(`${shareText}\n\n${leaderboardUrl}`)

      const urls = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&summary=${encodedText}`,
        discord: `https://discord.com/channels/@me`,
        email: `mailto:?subject=Can you beat my IQ test score?&body=${emailBody}`
      }

      if (platform === 'discord') {
        // For Discord, show copy modal with message
        // When user clicks "Copy Message", it will copy and open Discord
        setCopyModalAction('discord')
        setShowCopyModal(true)
        await trackShareAction('discord')
        setIsSharing(false)
        return
      } else if (platform === 'email' && isMobile) {
        // On mobile, use location.href for mailto: to open native email client
        window.location.href = urls.email
        await trackShareAction('email')
      } else if (urls[platform]) {
        // Open share window
        const shareWindow = window.open(
          urls[platform],
          '_blank',
          'width=600,height=400,scrollbars=yes,resizable=yes'
        )

        // Track share action
        await trackShareAction(platform)

        // Focus the share window
        if (shareWindow) {
          shareWindow.focus()
        }
      }

      onShare?.(platform, { text: shareText, url: leaderboardUrl })
    } catch (error) {
      console.error('Share failed:', error)
    } finally {
      setIsSharing(false)
    }
  }

  const trackShareAction = async (platform) => {
    try {
      if (!result?.id) return
      await apiCall('/social/track-share', {
        method: 'POST',
        body: JSON.stringify({
          resultId: result.id,
          platform,
          settings: shareSettings
        })
      })
    } catch (error) {
      // Silent fail for analytics
      console.log('Analytics tracking failed:', error)
    }
  }

  const handleDownloadImage = async () => {
    try {
      setIsSharing(true)
      
      // Generate result card image
      const canvas = await generateResultCardImage(result, shareSettings)
      
      // Download the image
      const link = document.createElement('a')
      link.download = `iq-test-result-${iqScore}.png`
      link.href = canvas.toDataURL()
      link.click()
      
      // Track download action
      await trackShareAction('download')
    } catch (error) {
      console.error('Download failed:', error)
      alert('Failed to generate image. Please try again.')
    } finally {
      setIsSharing(false)
    }
  }

  const generateResultCardImage = (result, settings) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      // Set canvas size
      canvas.width = 600
      canvas.height = 400
      
      // Create gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
      gradient.addColorStop(0, '#3B82F6') // primary-500
      gradient.addColorStop(1, '#8B5CF6') // secondary-500
      
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      
      // Add text
      ctx.fillStyle = 'white'
      ctx.textAlign = 'center'
      
      // IQ Score
      ctx.font = 'bold 72px Arial'
      ctx.fillText(result.iqScore || '120', canvas.width / 2, 150)
      
      // IQ Score label
      ctx.font = '24px Arial'
      ctx.fillText('IQ Score', canvas.width / 2, 190)
      
      // Classification
      if (settings.includeClassification) {
        ctx.font = 'bold 20px Arial'
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
        ctx.fillText(result.classification || 'Above Average', canvas.width / 2, 250)
      }
      
      // Percentile
      if (settings.includePercentile && result.percentile) {
        ctx.font = '16px Arial'
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
        ctx.fillText(`Better than ${result.percentile}% of test takers`, canvas.width / 2, 290)
      }
      
      // Website URL
      ctx.font = '14px Arial'
      ctx.fillText('Take the test at: ' + window.location.origin + '/leaderboard', canvas.width / 2, 350)
      
      resolve(canvas)
    })
  }

  const handlePrivacyChange = (privacy) => {
    setShareSettings(prev => ({ ...prev, privacy }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      {/* Mobile Layout */}
      {isMobile && (
        <div className="w-full max-w-sm">
          <MobileShareModal
            result={result}
            shareText={shareText}
            shareSettings={shareSettings}
            shareStats={shareStats}
            copied={copied}
            isSharing={isSharing}
            userProfile={userProfile}
            onClose={onClose}
            onCopyLink={handleCopyLink}
            onSocialShare={handleSocialShare}
            onDownloadImage={handleDownloadImage}
            onSettingsChange={setShareSettings}
            onPrivacyChange={handlePrivacyChange}
          />
        </div>
      )}

      {/* Desktop Layout */}
      {!isMobile && (
        <div className="w-full max-w-5xl">
          <DesktopShareModal
            result={result}
            shareText={shareText}
            shareSettings={shareSettings}
            shareStats={shareStats}
            shareUrl={shareUrl}
            copied={copied}
            isSharing={isSharing}
            userProfile={userProfile}
            onClose={onClose}
            onCopyLink={handleCopyLink}
            onSocialShare={handleSocialShare}
            onDownloadImage={handleDownloadImage}
            onSettingsChange={setShareSettings}
            onPrivacyChange={handlePrivacyChange}
          />
        </div>
      )}

      {/* Copy Message Modal */}
      <CopyMessageModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        message={`${shareText}\n\n${shareUrl || `${window.location.origin}/leaderboard`}`}
        title={copyModalAction === 'discord' ? 'Share on Discord' : 'Share Your Challenge'}
        onCopySuccess={handleCopySuccess}
      />
    </div>
  )
}

// Mobile Share Modal
const MobileShareModal = ({
  result,
  shareText,
  shareSettings,
  shareStats,
  copied,
  isSharing,
  userProfile,
  onClose,
  onCopyLink,
  onSocialShare,
  onDownloadImage,
  onSettingsChange,
  onPrivacyChange
}) => (
  <Card adaptive className="max-h-[90vh] overflow-y-auto">
    <CardHeader>
      <div className="flex justify-between items-center">
        <CardTitle>Share Your Results</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>
    </CardHeader>
    
    <CardContent className="space-y-6">
      {/* Result Preview */}
      <ResultCard result={result} settings={shareSettings} mobile />
      
      {/* Privacy Controls */}
      {userProfile && (
        <div className="space-y-3">
          <h3 className="font-semibold text-text-primary flex items-center gap-2">
            <Lock className="w-4 h-4" />
            Privacy
          </h3>
          <div className="flex gap-2">
            {['public', 'friends', 'private'].map((privacy) => (
              <button
                key={privacy}
                onClick={() => onPrivacyChange(privacy)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  shareSettings.privacy === privacy
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {privacy.charAt(0).toUpperCase() + privacy.slice(1)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quick Settings */}
      <div className="space-y-3">
        <h3 className="font-semibold text-text-primary">What to include</h3>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={shareSettings.includeScore}
              onChange={(e) => onSettingsChange(prev => ({ ...prev, includeScore: e.target.checked }))}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm">IQ score</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={shareSettings.includePercentile}
              onChange={(e) => onSettingsChange(prev => ({ ...prev, includePercentile: e.target.checked }))}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm">Percentile ranking</span>
          </label>
        </div>
      </div>
      
      {/* Share Options */}
      <div className="space-y-3">
        <h3 className="font-semibold text-text-primary">Share on social media</h3>
        <Grid cols={2}>
          <ShareButton
            icon={<Facebook className="w-5 h-5" />}
            label="Facebook"
            color="bg-[#1877F2] hover:bg-[#166FE5]"
            onClick={() => onSocialShare('facebook')}
            disabled={isSharing}
          />
          <ShareButton
            icon={<Twitter className="w-5 h-5" />}
            label="Twitter"
            color="bg-[#1DA1F2] hover:bg-[#1A91DA]"
            onClick={() => onSocialShare('twitter')}
            disabled={isSharing}
          />
          <ShareButton
            icon={<Linkedin className="w-5 h-5" />}
            label="LinkedIn"
            color="bg-[#0A66C2] hover:bg-[#004182]"
            onClick={() => onSocialShare('linkedin')}
            disabled={isSharing}
          />
          <ShareButton
            icon={<DiscordIcon className="w-5 h-5" />}
            label="Discord"
            color="bg-[#5865F2] hover:bg-[#4752C4]"
            onClick={() => onSocialShare('discord')}
            disabled={isSharing}
          />
          <ShareButton
            icon={<Mail className="w-5 h-5" />}
            label="Email"
            color="bg-neutral-600 hover:bg-neutral-700"
            onClick={() => onSocialShare('email')}
            disabled={isSharing}
          />
          <ShareButton
            icon={<MessageCircle className="w-5 h-5" />}
            label="SMS/WhatsApp"
            color="bg-green-600 hover:bg-green-700"
            onClick={() => onSocialShare('native')}
            disabled={isSharing}
          />
        </Grid>
      </div>
      
      {/* Copy Link */}
      <div className="space-y-3">
        <h3 className="font-semibold text-text-primary">Copy link</h3>
        <Button
          variant="outline"
          className="w-full"
          leftIcon={copied ? <Copy className="w-4 h-4 text-success-600" /> : <Link className="w-4 h-4" />}
          onClick={onCopyLink}
          disabled={isSharing}
        >
          {copied ? 'Copied!' : 'Copy link to share'}
        </Button>
      </div>
      
      {/* Download */}
      <div className="space-y-3">
        <h3 className="font-semibold text-text-primary">Save image</h3>
        <Button
          variant="outline"
          className="w-full"
          leftIcon={<Download className="w-4 h-4" />}
          onClick={onDownloadImage}
          disabled={isSharing}
        >
          {isSharing ? 'Generating...' : 'Download Result Card'}
        </Button>
      </div>

      {/* Share Stats */}
      {shareStats && (shareStats.views > 0 || shareStats.shares > 0) && (
        <div className="pt-4 border-t border-gray-200">
          <div className="flex justify-between text-sm text-gray-500">
            <span>{shareStats.views} views</span>
            <span>{shareStats.shares} shares</span>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
)

// Desktop Share Modal
const DesktopShareModal = ({
  result,
  shareText,
  shareSettings,
  shareStats,
  shareUrl,
  copied,
  isSharing,
  userProfile,
  onClose,
  onCopyLink,
  onSocialShare,
  onDownloadImage,
  onSettingsChange,
  onPrivacyChange
}) => (
  <Card adaptive className="max-h-[90vh] overflow-hidden">
    <CardHeader>
      <div className="flex justify-between items-center">
        <CardTitle className="text-2xl">Share Your IQ Test Results</CardTitle>
        <Button variant="ghost" onClick={onClose}>
          <X className="w-5 h-5" />
        </Button>
      </div>
    </CardHeader>

    <CardContent className="p-0">
      <div className="grid grid-cols-[1fr_420px] max-h-[calc(90vh-80px)]">
        {/* Left Panel - Preview */}
        <div className="p-6 border-r border-border-primary overflow-y-auto max-h-[calc(90vh-80px)]">
          <h3 className="text-lg font-semibold mb-4">Preview</h3>
          <div className="space-y-6">
            <ResultCard result={result} settings={shareSettings} desktop />

            {/* Share Text Preview */}
            <div className="p-4 bg-neutral-50 rounded-lg">
              <h4 className="font-medium mb-2">Share message:</h4>
              <p className="text-sm text-text-secondary whitespace-pre-wrap">{shareText}</p>
            </div>

            {/* Custom Message Input */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Add custom message (optional)
              </label>
              <textarea
                value={shareSettings.customMessage}
                onChange={(e) => onSettingsChange(prev => ({ ...prev, customMessage: e.target.value }))}
                placeholder="Add your own message..."
                className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                maxLength={280}
              />
              <div className="text-xs text-gray-500 mt-1">
                {shareSettings.customMessage.length}/280 characters
              </div>
            </div>

            {/* Share Stats */}
            {shareStats && (shareStats.views > 0 || shareStats.shares > 0) && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Share Performance</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{shareStats.views}</div>
                    <div className="text-blue-700">Views</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{shareStats.shares}</div>
                    <div className="text-blue-700">Shares</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Options */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="space-y-6">
            {/* Privacy Controls */}
            {userProfile && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5" />
                  <h3 className="font-semibold">Privacy Settings</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { value: 'public', label: 'Public', desc: 'Anyone can view your results', icon: <Eye className="w-4 h-4" /> },
                    { value: 'friends', label: 'Friends Only', desc: 'Only your connections can view', icon: <Users className="w-4 h-4" /> },
                    { value: 'private', label: 'Private', desc: 'Only you can view the link', icon: <EyeOff className="w-4 h-4" /> }
                  ].map((privacy) => (
                    <label key={privacy.value} className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="privacy"
                        value={privacy.value}
                        checked={shareSettings.privacy === privacy.value}
                        onChange={(e) => onPrivacyChange(e.target.value)}
                        className="mt-1 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 font-medium text-sm">
                          {privacy.icon}
                          {privacy.label}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{privacy.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Customization */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Settings className="w-5 h-5" />
                <h3 className="font-semibold">Customize Content</h3>
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={shareSettings.includeScore}
                    onChange={(e) => onSettingsChange(prev => ({ ...prev, includeScore: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Include IQ score</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={shareSettings.includePercentile}
                    onChange={(e) => onSettingsChange(prev => ({ ...prev, includePercentile: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Include percentile ranking</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={shareSettings.includeClassification}
                    onChange={(e) => onSettingsChange(prev => ({ ...prev, includeClassification: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Include classification</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={shareSettings.includeComparison}
                    onChange={(e) => onSettingsChange(prev => ({ ...prev, includeComparison: e.target.checked }))}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Include comparison chart</span>
                </label>
              </div>
            </div>
            
            {/* Social Platforms */}
            <div>
              <h3 className="font-semibold mb-4">Share on social media</h3>
              <div className="space-y-2">
                <ShareButton
                  icon={<Facebook className="w-5 h-5" />}
                  label="Share on Facebook"
                  color="bg-[#1877F2] hover:bg-[#166FE5]"
                  onClick={() => onSocialShare('facebook')}
                  disabled={isSharing}
                  fullWidth
                />
                <ShareButton
                  icon={<Twitter className="w-5 h-5" />}
                  label="Share on Twitter"
                  color="bg-[#1DA1F2] hover:bg-[#1A91DA]"
                  onClick={() => onSocialShare('twitter')}
                  disabled={isSharing}
                  fullWidth
                />
                <ShareButton
                  icon={<Linkedin className="w-5 h-5" />}
                  label="Share on LinkedIn"
                  color="bg-[#0A66C2] hover:bg-[#004182]"
                  onClick={() => onSocialShare('linkedin')}
                  disabled={isSharing}
                  fullWidth
                />
                <ShareButton
                  icon={<DiscordIcon className="w-5 h-5" />}
                  label="Share on Discord"
                  color="bg-[#5865F2] hover:bg-[#4752C4]"
                  onClick={() => onSocialShare('discord')}
                  disabled={isSharing}
                  fullWidth
                />
                <ShareButton
                  icon={<Mail className="w-5 h-5" />}
                  label="Share via Email"
                  color="bg-neutral-600 hover:bg-neutral-700"
                  onClick={() => onSocialShare('email')}
                  disabled={isSharing}
                  fullWidth
                />
                <ShareButton
                  icon={<MessageCircle className="w-5 h-5" />}
                  label="Share via SMS/WhatsApp"
                  color="bg-green-600 hover:bg-green-700"
                  onClick={() => onSocialShare('native')}
                  disabled={isSharing}
                  fullWidth
                />
              </div>
            </div>
            
            {/* Direct Actions */}
            <div>
              <h3 className="font-semibold mb-4">Direct actions</h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  leftIcon={copied ? <Copy className="w-4 h-4 text-success-600" /> : <Link className="w-4 h-4" />}
                  onClick={onCopyLink}
                  disabled={isSharing}
                >
                  {copied ? 'Link Copied!' : 'Copy link to share'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  leftIcon={<Image className="w-4 h-4" />}
                  onClick={onDownloadImage}
                  disabled={isSharing}
                >
                  {isSharing ? 'Generating Image...' : 'Download as Image'}
                </Button>
              </div>
            </div>

            {/* Share URL Display */}
            {shareUrl && (
              <div>
                <h3 className="font-semibold mb-2">Share URL</h3>
                <div className="p-2 bg-gray-100 rounded text-xs font-mono break-all">
                  {shareUrl}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
)

// Result Card Component
const ResultCard = ({ result, settings = {}, mobile = false, desktop = false }) => {
  const { 
    iqScore = 120, 
    percentile = 75, 
    classification = 'Above Average',
    testType = 'free',
    categoryBreakdown = {}
  } = result
  
  const {
    includeScore = true,
    includePercentile = true,
    includeClassification = true,
    includeComparison = false
  } = settings
  
  return (
    <div className={`bg-gradient-to-br from-primary-500 to-secondary-500 text-white rounded-lg p-6 ${desktop ? 'max-w-md' : ''}`}>
      <div className="text-center">
        {includeScore && (
          <>
            <div className="text-4xl font-bold mb-2">{iqScore}</div>
            <div className="text-lg opacity-90 mb-4">IQ Score</div>
          </>
        )}
        
        {includeClassification && (
          <div className="bg-white/20 rounded-full px-4 py-2 inline-block mb-4">
            <span className="font-medium">{classification}</span>
          </div>
        )}
        
        {includePercentile && percentile && (
          <div className="text-sm opacity-75 mb-4">
            Better than {percentile}% of test takers
          </div>
        )}

        {includeComparison && Object.keys(categoryBreakdown).length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="text-xs opacity-75 mb-2">Category Performance</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(categoryBreakdown).map(([category, score]) => (
                <div key={category} className="bg-white/10 rounded px-2 py-1">
                  <div className="font-medium">{category}</div>
                  <div className="opacity-75">{score}%</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-white/20 text-xs opacity-60">
          {testType === 'comprehensive' ? 'Comprehensive Test' : 'Free Test'} • Take yours at {window.location.hostname}
        </div>
      </div>
    </div>
  )
}

// Share Button Component
const ShareButton = ({ icon, label, color, onClick, fullWidth = false, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`
      ${fullWidth ? 'w-full justify-start' : 'aspect-square'} 
      flex items-center ${fullWidth ? 'gap-3 px-4 py-3' : 'justify-center p-3'} 
      rounded-lg text-white font-medium transition-all duration-200 
      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
      ${disabled 
        ? 'opacity-50 cursor-not-allowed' 
        : 'hover:scale-105 active:scale-95'
      }
      ${color}
    `}
  >
    {icon}
    {fullWidth && <span>{label}</span>}
  </button>
)

export default ShareModal